/**
 * Persian Text Renderer Utility
 * Handles proper Persian/Farsi text rendering in Canvas with font loading and RTL support
 */

export interface TextRenderOptions {
  fontSize?: number;
  fontWeight?: string;
  color?: string;
  backgroundColor?: string;
  padding?: number;
  lineHeight?: number;
  maxWidth?: number;
  textAlign?: 'center' | 'right' | 'left';
}


export class PersianTextRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  
  constructor(width: number = 400, height: number = 600) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = width;
    this.canvas.height = height;
    
    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas context not available');
    }
    this.ctx = ctx;
  }

  /**
   * Load Persian fonts and ensure they're ready for use
   */
  async loadPersianFonts(): Promise<void> {
    try {
      if ('fonts' in document) {
        await document.fonts.ready;
        
        // List of Persian-compatible fonts to try loading
        // const fontUrls = [];

        // Try to load fonts
        // for (const url of fontUrls) {
        //   try {
        //     const link = document.createElement('link');
        //     link.href = url;
        //     link.rel = 'stylesheet';
        //     document.head.appendChild(link);
            
        //     // Wait a bit for font to load
        //     await new Promise(resolve => setTimeout(resolve, 100));
        //   } catch (error) {
        //     console.warn('Failed to load font:', url);
        //   }
        // }
      }
    } catch (error) {
      console.warn('Font loading not supported:', error);
    }
  }

  /**
   * Get the best available Persian font
   */
  private getPersianFontFamily(): string {
    const fonts = [
      'Vazirmatn',
      'Tahoma',
      'Arial Unicode MS', 
      'Segoe UI',
      'Noto Sans Arabic',
      'system-ui',
      'sans-serif'
    ];
    
    return fonts.join(', ');
  }

  /**
   * Wrap text to fit within specified width
   */
  private wrapText(text: string, maxWidth: number): string[] {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = this.ctx.measureText(testLine);
      
      if (metrics.width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    return lines;
  }

  /**
   * Calculate optimal font size for text to fit in canvas
   */
  private calculateOptimalFontSize(
    text: string, 
    maxWidth: number, 
    maxHeight: number, 
    minFontSize: number = 16,
    maxFontSize: number = 48
  ): number {
    let fontSize = maxFontSize;
    
    while (fontSize >= minFontSize) {
      this.ctx.font = `bold ${fontSize}px ${this.getPersianFontFamily()}`;
      
      const lines = text.split('\n').flatMap(line => 
        line.trim() ? this.wrapText(line.trim(), maxWidth) : ['']
      );
      
      const totalHeight = lines.length * fontSize * 1.2;
      
      if (totalHeight <= maxHeight) {
        return fontSize;
      }
      
      fontSize -= 2;
    }
    
    return minFontSize;
  }

  /**
   * Apply background gradient or solid color
   */
  private applyBackground(backgroundType: string): void {
    if (backgroundType.includes('gradient')) {
      const gradient = this.ctx.createLinearGradient(0, 0, this.canvas.width, this.canvas.height);
      
      if (backgroundType.includes('purple') && backgroundType.includes('pink')) {
        gradient.addColorStop(0, '#8b5cf6');
        gradient.addColorStop(1, '#ec4899');
      } else if (backgroundType.includes('blue') && backgroundType.includes('purple')) {
        gradient.addColorStop(0, '#3b82f6');
        gradient.addColorStop(1, '#8b5cf6');
      } else if (backgroundType.includes('green') && backgroundType.includes('blue')) {
        gradient.addColorStop(0, '#10b981');
        gradient.addColorStop(1, '#3b82f6');
      } else if (backgroundType.includes('yellow') && backgroundType.includes('red')) {
        gradient.addColorStop(0, '#f59e0b');
        gradient.addColorStop(1, '#ef4444');
      } else if (backgroundType.includes('pink') && backgroundType.includes('yellow')) {
        gradient.addColorStop(0, '#ec4899');
        gradient.addColorStop(1, '#f59e0b');
      } else if (backgroundType.includes('indigo') && backgroundType.includes('purple')) {
        gradient.addColorStop(0, '#6366f1');
        gradient.addColorStop(1, '#8b5cf6');
      } else {
        gradient.addColorStop(0, '#8b5cf6');
        gradient.addColorStop(1, '#ec4899');
      }
      
      this.ctx.fillStyle = gradient;
    } else if (backgroundType.includes('black')) {
      this.ctx.fillStyle = '#000000';
    } else if (backgroundType.includes('white')) {
      this.ctx.fillStyle = '#ffffff';
    } else {
      this.ctx.fillStyle = '#8b5cf6';
    }
    
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /**
   * Render Persian text with proper formatting
   */
  async renderText(text: string, options: TextRenderOptions = {}): Promise<Blob | null> {
    const {
      color = '#ffffff',
      backgroundColor = 'bg-gradient-to-br from-purple-500 to-pink-500',
      padding = 40,
      textAlign = 'center'
    } = options;

    // Load fonts first
    await this.loadPersianFonts();

    // Apply background
    this.applyBackground(backgroundColor);

    // Calculate available space
    const maxWidth = this.canvas.width - (padding * 2);
    const maxHeight = this.canvas.height - (padding * 2);

    // Calculate optimal font size
    const fontSize = this.calculateOptimalFontSize(text, maxWidth, maxHeight);

    // Set text properties
    this.ctx.fillStyle = backgroundColor.includes('white') ? '#000000' : color;
    this.ctx.textAlign = textAlign;
    this.ctx.textBaseline = 'middle';
    this.ctx.direction = 'rtl';
    this.ctx.font = `bold ${fontSize}px ${this.getPersianFontFamily()}`;

    // Process and render text
    const inputLines = text.split('\n');
    const allLines: string[] = [];
    
    for (const line of inputLines) {
      if (line.trim()) {
        const wrappedLines = this.wrapText(line.trim(), maxWidth);
        allLines.push(...wrappedLines);
      } else {
        allLines.push('');
      }
    }

    // Calculate positioning
    const lineHeight = fontSize * 1.2;
    const totalTextHeight = allLines.length * lineHeight;
    const startY = (this.canvas.height - totalTextHeight) / 2 + lineHeight / 2;

    // Render each line
    allLines.forEach((line, index) => {
      const y = startY + index * lineHeight;
      let x: number;
      
      switch (textAlign) {
        case 'right':
          x = this.canvas.width - padding;
          break;
        case 'left':
          x = padding;
          break;
        default: // center
          x = this.canvas.width / 2;
      }
      
      this.ctx.fillText(line, x, y);
    });

    // Convert to blob
    return new Promise((resolve) => {
      this.canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/png', 0.9);
    });
  }

  /**
   * Get the canvas element (for debugging or preview)
   */
  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }
}